import React, { forwardRef, Ref } from 'react';
import styled from 'styled-components';
import { ReactSelectChangeEvent, ReactSelectOption, InputChange } from '../types';
import ReactSelect from 'react-select';
import BaseButton from './BaseButton';
import ClearInputButton from './ClearInputButton';

interface Props {
   dropdownPosition: 'left' | 'right';
   inputPlaceholder: string;
   selectOnChange: ReactSelectChangeEvent;
   selectValue: ReactSelectOption;
   selectOptions: ReactSelectOption[];
   buttonText: string;
   isPreviousSearch: boolean;
   inputValue: string;
   inputOnChange: (e: InputChange) => void;
   searchDisabled: boolean;
   clearSearchAndRefireQuery: () => void;
   showInputLoadingState: boolean;
   clearInputValue: () => void;
   onClick: () => void;
}

const InputWithDropdown = forwardRef(
   (
      {
         dropdownPosition,
         inputPlaceholder,
         selectOnChange,
         selectValue,
         selectOptions,
         buttonText,
         isPreviousSearch,
         inputOnChange,
         searchDisabled,
         clearSearchAndRefireQuery,
         showInputLoadingState,
         clearInputValue,
         inputValue,
         onClick,
      }: Props,
      ref: Ref<HTMLInputElement>
   ) => {
      const borderRadius = '10px';
      const selectStyles = {
         borderTopLeftRadius: dropdownPosition === 'left' ? borderRadius : 0,
         borderBottomLeftRadius: dropdownPosition === 'left' ? borderRadius : 0,
         borderTopRightRadius: 0,
         borderBottomRightRadius: 0,
         borderLeft: dropdownPosition === 'right' ? 'none' : '1px solid #CCCCCC',
         border: '1px solid #CCCCCC',
         boxShadow: 'none',
         '&:hover': { cursor: 'pointer', border: '1px solid #CCCCCC', boxShadow: 'none' },
      };

      //ensure that a request is made on clear only if there was a prior search made -- otherwise if the user is just clearing the input we don't want to refire query
      const handleClearSearch = async () => (isPreviousSearch ? await clearSearchAndRefireQuery() : clearInputValue());

      const SelectInput = (): JSX.Element => (
         <ReactSelect
            className="basic-single"
            value={selectValue}
            classNamePrefix="select"
            defaultValue={selectOptions[0]}
            onChange={selectOnChange}
            options={selectOptions}
            styles={{
               container: base => ({ ...base, flex: 1 }),
               control: base => ({ ...base, ...selectStyles }),
               option: base => ({ ...base, '&:hover': { cursor: 'pointer' } }),
            }}
         />
      );

      return (
         <Container>
            {dropdownPosition === 'left' && <SelectInput />}
            <Input
               dropdownPosition={dropdownPosition}
               placeholder={inputPlaceholder}
               borderRadius={borderRadius}
               onChange={inputOnChange}
               value={inputValue}
               type="text"
               ref={ref}
            />

            <ClearInputButton visible={!!inputValue || showInputLoadingState} onClick={handleClearSearch} loading={showInputLoadingState} />
            {dropdownPosition === 'right' && <SelectInput />}
            <InputButton onClick={onClick} disabled={searchDisabled}>
               {buttonText.toLocaleUpperCase()}
            </InputButton>
         </Container>
      );
   }
);

export default InputWithDropdown;

const Container = styled.div`
   display: flex;
   position: relative;
   flex-direction: row;
   justify-content: center;
   align-items: center;
   width: 100%;
`;

const Input = styled.input.attrs(({ type = 'text' }) => ({ type }))<Pick<Props, 'dropdownPosition'> & { borderRadius: string }>`
   font-size: 0.9em;
   color: #333333;
   background: white;
   height: 28px;
   border: 1px solid #cccccc;
   box-sizing: border-box;
   padding: 22px 20px;
   border-top-left-radius: ${({ dropdownPosition, borderRadius }) => (dropdownPosition === 'left' ? 0 : borderRadius)};
   border-bottom-left-radius: ${({ dropdownPosition, borderRadius }) => (dropdownPosition === 'left' ? 0 : borderRadius)};
   border-top-right-radius: ${({ dropdownPosition, borderRadius }) => (dropdownPosition === 'right' ? 0 : borderRadius)};
   border-bottom-right-radius: ${({ dropdownPosition, borderRadius }) => (dropdownPosition === 'right' ? 0 : borderRadius)};
   flex-grow: 1;
   ::placeholder {
      color: ${({ theme }) => theme.primaryGrey};
      opacity: 0.9;
      font-style: italic;
   }
   @media only screen and (max-width: 590px) {
      margin-bottom: 7px;
   }
`;

const InputButton = styled(BaseButton)`
   width: auto;
   padding: 10px 15px;
   margin: unset;
   flex-grow: 0;
   border-top-left-radius: 0;
   border-bottom-left-radius: 0;
   &:focus {
      border: none;
   }
`;
